/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

/*
Useful when you have a large but fixed set of markers to show.

Stores all markers in a quad-tree, and only displays one marker per tile (density parameter determines tile size)

This class is not useful when you are loading marker tiles on demand (i.e. the number of markers is too many to load into memory)
*/

function quadTree(maxZ) {
	var tiles = {};
	// TODO: show all markers on highest zoom level, then we can make this more sensible ~16
	maxZ = maxZ || 25;

	function add(x, y, data, priority) {
		for(var tz = maxZ; tz >= 0; tz--) {
			var zz = Math.pow(2, tz);
			var tx = Math.floor(x * zz);
			var ty = Math.floor(y * zz);
			var key = tz + "_" + tx + "_" + ty;
			if(tz == maxZ) {
				if(key in tiles) {
					tiles[key].push(data);
				} else {
					tiles[key] = [data];
				}
			} else {
				if(!(key in tiles) || tiles[key].priority > priority) {
					tiles[key] = { data: data, priority: priority }
				} else {
					break;
				}
			}
		}
	}

	function get(tx, ty, tz) {
		var key = tz + "_" + tx + "_" + ty;
		if(tz == maxZ) {
			return tiles[key] || [];
		} else {
			var t = tiles[key];
			return t && t.data;
		}
	}

	function setMaxZ(z) {
		maxZ = z;
	}

	return { add, get, setMaxZ };
}

function always(x) {
	return function() {
		return x;
	}
}

function layer(map, options) {
	options = options || {};
	options.density = options.density || 2;

	var markerTree = quadTree();
	var displayedTiles = {};

	var markerPool = [];

	var markers = [];
	var markerOptions = [];

	var rebuildRequired = false;
	var filterFn = always(true);
	var priorityFn = always(1);

	function addMarker(marker) {
		rebuildRequired = true;
		markers.push(marker);

		for(const [eventName, handler] of Object.entries(options.listeners || {})) {
			google.maps.event.addListener(marker, eventName, handler);
		}
	}

	function addMarkerOptions(markerOpt) {
		rebuildRequired = true;
		markerOptions.push(markerOpt);
	}

	function removeAll() {
		markerTree = quadTree();
		markers = [];
		markerOptions = [];
	}

	function setFilter(filter) {
		filterFn = filter || always(true);
		rebuildRequired = true;
		scheduleRefresh();
	}

	function setPriorityFn(priority) {
		priorityFn = priority || always(1);
		rebuildRequired = true;
		scheduleRefresh();
	}

	function getMarker(lazyMarker) {
		if(!lazyMarker.marker) {
			var marker = markerPool.pop();
			if(marker) {
				marker.setOptions(lazyMarker.markerOptions);
			} else {
				marker = new google.maps.Marker(lazyMarker.markerOptions);
				for(const [eventName, handler] of Object.entries(options.listeners || {})) {
					google.maps.event.addListener(marker, eventName, handler);
				}
			}
			lazyMarker.showCount = 0;
			lazyMarker.marker = marker;
		}
		return lazyMarker.marker;
	}

	function releaseMarker(lazyMarker) {
		var marker = lazyMarker.marker;
		marker.setMap(null);

		if(lazyMarker.markerOptions) {
			markerPool.push(marker);
			lazyMarker.marker = null;
		}
	}

	function addTile(tile) {
		var lazyMarker = markerTree.get(tile.x, tile.y, tile.z);

		if(lazyMarker) {
			var marker = getMarker(lazyMarker);
			tile.lazyMarker = lazyMarker;
			if(!marker.getMap()) {
				marker.setMap(map);
			}
			lazyMarker.showCount++;
		}

		return tile;
	}

	function updateTile(tile) {
		var lazyMarker = markerTree.get(tile.x, tile.y, tile.z);
		if(lazyMarker) {
			if(tile.lazyMarker) {
				if(tile.lazyMarker.marker != lazyMarker.marker) {
					removeTile(tile);
					addTile(tile);
				}
			} else {
				addTile(tile);
			}
		} else {
			if(tile.lazyMarker) {
				removeTile(tile);
			}
		}
	}

	function removeTile(tile) {
		var lazyMarker = tile.lazyMarker;
		if(lazyMarker) {
			lazyMarker.showCount--;
			if(lazyMarker.showCount == 0) {
				releaseMarker(lazyMarker);
				tile.lazyMarker = null;
			}
		}
	}

	function rebuildIfNecessary() {
		if(rebuildRequired) {
			var t0 = performance.now();

			rebuildRequired = false;
			var proj = map.getProjection();

			markerTree = quadTree(options.maxZoom + options.density + 1);

			markers.forEach(marker => {
				var markerInfo = { marker: marker, showCount: 0};
				if(filterFn(markerInfo)) {
					var p = proj.fromLatLngToPoint(marker.getPosition());
					markerTree.add(p.x/256, p.y/256, markerInfo, priorityFn(markerInfo));
				}
			});
			markerOptions.forEach(markerOpt => {
				var markerInfo = {markerOptions: markerOpt, showCount: 0};
				if(filterFn(markerInfo)) {
					var p = proj.fromLatLngToPoint(new google.maps.LatLng(markerOpt.position.lat, markerOpt.position.lng));
					markerTree.add(p.x/256, p.y/256, markerInfo, priorityFn(markerInfo));
				}
			});

			console.log("Rebuilt markers in " + (performance.now() - t0) + "ms");
		}
	}

	var visible = true;

	function updateOverlay() {
		rebuildIfNecessary();

		var tiles = visible ? tilesInView() : {};

		var newKeys = new Set(Object.keys(tiles));
		var oldKeys = new Set(Object.keys(displayedTiles));

		newKeys.forEach(function(key) {
			if(!oldKeys.has(key)) {
				displayedTiles[key] = addTile(tiles[key]);
			}
		});
		oldKeys.forEach(function(key) {
			if(!newKeys.has(key)) {
				removeTile(displayedTiles[key]);
				delete displayedTiles[key];
			}
		});
	}

	function setVisible(isVisible) {
		visible = isVisible;
		updateOverlay();
	}

	var refreshScheduled = false;
	function scheduleRefresh() {
		if(!refreshScheduled) {
			setTimeout(function() {
				refreshScheduled = false;
				refresh();
			});
		}
	}

	function refresh() {
		if(!map.getProjection()) {
			return;
		}

		rebuildRequired = true;
		rebuildIfNecessary();

		Object.values(displayedTiles).forEach(updateTile);

		updateOverlay();
	}

	function tilesInView() {
		var proj = map.getProjection();
		if(!proj) {
			return {};
		}

		var z = map.getZoom();
		var sw = proj.fromLatLngToPoint(map.getBounds().getSouthWest());
		var ne = proj.fromLatLngToPoint(map.getBounds().getNorthEast());
		var x, y;

		if(options.maxZoom && z > options.maxZoom) return {};
		if(options.minZoom && z < options.minZoom) return {};

		z = Math.min(z + options.density, 24);

		var zz = Math.pow(2,z);

		if(sw.x > ne.x) sw.x -= 256;

		var scale = zz / 256;

		sw.x *= scale; sw.y *= scale;
		ne.x *= scale; ne.y *= scale;

		var tiles = {};
		for(x = Math.floor(sw.x); x < Math.ceil(ne.x); x++) {
			for(y = Math.floor(ne.y); y < Math.ceil(sw.y); y++) { // y is flipped
				var tx = (x + zz) % zz;
				var key = tx + "," + y + "," + z;
				tiles[key] = {
					x: tx,
					y: y,
					z: z,
					key: key
				};
			}
		}

		return tiles;
	}

	google.maps.event.addListener(map, 'bounds_changed', throttle(400, updateOverlay));

	return {
		addMarker: addMarker,
		addMarkerOptions: addMarkerOptions,
		setFilter: setFilter,
		setPriorityFn: setPriorityFn,
		refresh: refresh,
		setVisible: setVisible,
		removeAll: removeAll
	};
}

function throttle(delay, fn) {
	var scheduled = false;
	return function() {
		var args = arguments, thisArg = this;
		if(!scheduled) {
			scheduled = true;
			setTimeout(function() {
				fn.apply(thisArg, args);
				scheduled = false;
			}, delay);
		}
	}
}

export default {
	layer: layer
};
